import { forwardRef, MutableRefObject, useEffect, useImperativeHandle, useRef } from 'react';

import { GAME_MODE, PlayerSetup, Song } from '~/interfaces';
import { VideoPlayerRef, VideoState } from '~/modules/elements/video-player/index';
import CanvasDrawing from '~/modules/game-engine/drawing/index';
import fragShader from '~/modules/game-engine/drawing/shaders/shader.frag?raw';
import vertShader from '~/modules/game-engine/drawing/shaders/shader.vert?raw';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import RemoteMicServer from '~/modules/remote-mic/network/server';
import tuple from '~/modules/utils/tuple';
import SkipIntro from '~/routes/game/singing/game-overlay/components/skip-intro';
import SkipOutro from '~/routes/game/singing/game-overlay/components/skip-outro';
import getPlayerScoreData from '~/routes/game/singing/game-overlay/helpers/get-player-score-data';
import { GraphicSetting, MobilePhoneModeSetting, useSettingValue } from '~/routes/settings/settings-state';

import GameState from '../../../../modules/game-engine/game-state/game-state';
import DurationBar from './components/duration-bar';
import Lyrics from './components/lyrics';
import ScoreText from './components/score-text';

interface Props {
  song: Song;
  currentStatus: VideoState;
  width: number;
  height: number;
  onSongEnd?: () => void;
  playerSetups: PlayerSetup[];
  duration: number;
  effectsEnabled: boolean;
  videoPlayerRef: MutableRefObject<VideoPlayerRef | null>;
  isPauseMenuVisible: boolean;
  /** Online mode: only the host gets the skip-intro prompt. */
  skipIntroEnabled?: boolean;
  /** Online mode: routes the skip through the room server so it applies to everyone. */
  onSkipIntro?: (targetTimeSec: number) => void;
  /** Online mode: the player number leading the room, or null while nobody leads it (a tie, or no
   * scores yet). The local game leaves this undefined and derives the medal from the players on this
   * device — in a room that is only ever this singer, so everyone would be crowned first. */
  leadingPlayerNumber?: PlayerNumber | null;
  onOpenPauseMenu?: () => void;
}

const MAX_RENDER_RESOLUTION_W = 1920;

const GameOverlay = forwardRef(function (
  {
    currentStatus,
    width,
    height,
    playerSetups,
    onSongEnd,
    effectsEnabled,
    videoPlayerRef,
    isPauseMenuVisible,
    onOpenPauseMenu,
    duration,
    skipIntroEnabled = true,
    onSkipIntro,
    leadingPlayerNumber,
  }: Props,
  fRef,
) {
  'use no memo'; // React Compiler: reads GameState/PlayersManager mutable singletons directly during render (getPlayerScore, getSingSetup, getPlayers), so the compiler can cache stale JSX (e.g. the score display never updating).
  const [graphicLevel] = useSettingValue(GraphicSetting);
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const drawer = useRef<CanvasDrawing | null>(null);
  const lyrics = useRef<HTMLDivElement | null>(null);

  const overlayWidth = MAX_RENDER_RESOLUTION_W;
  const overlayHeight = overlayWidth * (height / width);

  const overlayScaleFactor = overlayHeight / height;

  useImperativeHandle(fRef, () => {
    return {
      pause: () => drawer.current?.pause(),
      resume: () => drawer.current?.resume(),
    };
  }, []);

  useEffect(() => {
    GameState.startInputMonitoring();
    // Phones report their ping once per song, so they need to know where a song begins and ends
    RemoteMicServer.callAllClients('songStarted');

    return () => {
      GameState.stopInputMonitoring();
      RemoteMicServer.callAllClients('songEnded');
    };
  }, []);

  // const resolutionScaleFactor = overlayWidth / MAX_RENDER_RESOLUTION_W;

  useEffect(() => {
    if (!canvas.current || !lyrics.current) return;

    drawer.current = new CanvasDrawing(
      canvas.current,
      lyrics.current.offsetHeight * overlayScaleFactor,
      // resolutionScaleFactor,
    );
    drawer.current.start();

    return () => {
      drawer.current?.end();
    };
  }, [overlayScaleFactor]);

  useEffect(() => {
    if (isPauseMenuVisible && drawer.current?.isPlaying()) {
      drawer.current?.pause();
    } else if (!isPauseMenuVisible && !drawer.current?.isPlaying()) {
      drawer.current?.resume();
    }
  }, [isPauseMenuVisible]);

  useEffect(() => {
    if (currentStatus === VideoState.ENDED && onSongEnd) {
      onSongEnd();
    }
  }, [currentStatus, onSongEnd]);

  const players = PlayersManager.getPlayers();
  const showMultipleLines = !mobilePhoneMode && players.length > 1;

  return (
    <div className="text-default relative flex h-full flex-col font-bold">
      {graphicLevel === 'high' && (
        <>
          <script type={'x-shader/x-fragment'} id={'plane-fs'}>
            {fragShader}
          </script>
          <script id="plane-vs" type="x-shader/x-vertex">
            {vertShader}
          </script>
          <div id="canvas" className="absolute inset-0 z-20 h-full w-full" />
        </>
      )}
      <div id="plane" className="absolute inset-0 h-full w-full" style={!effectsEnabled ? { opacity: 0 } : undefined}>
        <canvas
          ref={canvas}
          width={overlayWidth}
          height={overlayHeight}
          data-sampler="planeTexture"
          className="h-full w-full"
        />
      </div>
      {effectsEnabled && (
        <>
          {skipIntroEnabled && (
            <SkipIntro
              playerRef={videoPlayerRef}
              isEnabled={!isPauseMenuVisible}
              onSkip={onSkipIntro}
              onOpenPauseMenu={onOpenPauseMenu}
            />
          )}
          <SkipOutro onSongEnd={onSongEnd} isEnabled={!isPauseMenuVisible} onOpenPauseMenu={onOpenPauseMenu} />
        </>
      )}
      <DurationBar players={playerSetups} currentStatus={currentStatus} duration={duration} />
      <div className="py-5">
        {showMultipleLines && (
          <Lyrics player={players[0]} effectsEnabled={effectsEnabled} showStatusForAllPlayers={players.length > 2} />
        )}
      </div>
      <div className="stroke-text z-10 flex h-full flex-1 flex-col justify-around pr-4 text-right text-3xl max-lg:text-xl">
        {effectsEnabled && (
          <>
            {GameState.getSingSetup()?.mode === GAME_MODE.CO_OP ? (
              <span data-test="players-score" data-score={Math.floor(GameState.getPlayerScore(0))}>
                <ScoreText score={GameState.getPlayerScore(0)} />
              </span>
            ) : (
              PlayersManager.getPlayers().map((player) => {
                const scores = PlayersManager.getPlayers().map((player) =>
                  tuple([player.number, GameState.getPlayerScore(player.number)]),
                );
                const { score, isFirst } = getPlayerScoreData(scores, player.number);
                const isLeading = leadingPlayerNumber === undefined ? isFirst : leadingPlayerNumber === player.number;

                return (
                  <span
                    className="typography relative"
                    key={player.number}
                    data-test={`player-${player.number}-score`}
                    data-score={Math.floor(score)}>
                    <ScoreText score={score} />
                    <div
                      className={`absolute top-10 rotate-12 text-xl transition-all duration-200 max-lg:top-6 max-lg:text-lg ${isLeading ? '-right-2' : '-right-36'}`}>
                      {isLeading ? <div className="motion-preset-pulse-sm opacity-75">🥇</div> : '🥇'}
                    </div>
                  </span>
                );
              })
            )}
          </>
        )}
      </div>
      <div className="py-5" ref={lyrics}>
        <Lyrics
          showStatusForAllPlayers={players.length > 2}
          player={players.length > 2 ? players[0] : players.at(-1)!}
          bottom
          effectsEnabled={effectsEnabled}
        />
      </div>
    </div>
  );
});

export default GameOverlay;
